import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { FaArrowLeft, FaFileAlt, FaCalendarAlt, FaUser, FaBook, FaClipboardList } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchReportDetails, clearError, clearCurrentReport } from '../../redux/slices/studentReportsSlice';
import styles from './ReportDetails.module.css';

function ReportDetails() {
  const { isDarkMode } = useDarkMode();
  const { reportId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { currentReport, loading, error } = useSelector((state) => state.studentReports);
  const { currentUser } = useSelector((state) => state.auth);
  
  const userRole = currentUser?.role || localStorage.getItem("userRole");

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (reportId) {
      dispatch(fetchReportDetails(reportId));
    }
  }, [dispatch, reportId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleBack = () => {
    // تحديد الصفحة المناسبة حسب نوع المستخدم
    if (userRole === 'parent') {
      navigate('/parent-child-reports');
    } else if (userRole === 'teacher') {
      navigate('/students');
    } else {
      navigate('/');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`${styles.reportDetailsPage} ${isDarkMode ? styles.darkMode : ''}`}>
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>جاري تحميل تفاصيل التقرير...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentReport) {
    return (
      <div className={`${styles.reportDetailsPage} ${isDarkMode ? styles.darkMode : ''}`}>
        <div className={styles.container}>
          <div className={styles.errorContainer}>
            <h3>لا يمكن تحميل التقرير</h3>
            <p>حدث خطأ في تحميل تفاصيل التقرير</p>
            <button className={styles.backButton} onClick={handleBack}>
              العودة للصفحة السابقة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.reportDetailsPage} ${isDarkMode ? styles.darkMode : ''}`}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <FaArrowLeft />
            رجوع
          </button>
          
          <div className={styles.titleSection}>
            <h1>تفاصيل التقرير</h1>
            <p>عرض كامل تفاصيل التقرير</p>
          </div>
        </div>

        {/* Report Content */}
        <div className={styles.reportContent}>
          {/* Report Header Card */}
          <div className={styles.reportHeaderCard}>
            <div className={styles.reportIcon}>
              <FaFileAlt />
            </div>
            <div className={styles.reportInfo}>
              <h2>{currentReport.report_name || 'تقرير بدون عنوان'}</h2>
              <div className={styles.reportMeta}>
                <span className={styles.reportNumber}>تقرير #{currentReport.id}</span>
                <span className={styles.reportDate}>
                  <FaCalendarAlt />
                  {formatDate(currentReport.created_at)}
                </span>
              </div>
            </div>
          </div>

          {/* Student Info Card */}
          <div className={styles.infoCard}>
            <div className={styles.cardHeader}>
              <FaUser />
              <h3>معلومات الطالب</h3>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>اسم الطالب:</span>
                <span className={styles.infoValue}>{currentReport.student_name || 'غير محدد'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>رقم الطالب:</span>
                <span className={styles.infoValue}>{currentReport.student_id}</span>
              </div>
            </div>
          </div>

          {/* Report Types Card */}
          <div className={styles.infoCard}>
            <div className={styles.cardHeader}>
              <FaClipboardList />
              <h3>نوع التقرير</h3>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.reportTypes}>
                {currentReport.absence && (
                  <div className={styles.typeBadge}>
                    <span className={styles.badgeIcon}>📋</span>
                    <span>تقرير غياب</span>
                  </div>
                )}
                
                {currentReport.homework && (
                  <div className={styles.typeBadge}>
                    <span className={styles.badgeIcon}>📚</span>
                    <span>تقرير واجبات</span>
                  </div>
                )}
                
                {currentReport.weeknesses && (
                  <div className={styles.typeBadge}>
                    <span className={styles.badgeIcon}>⚠️</span>
                    <span>تقرير نقاط ضعف</span>
                  </div>
                )}
                
                {!currentReport.absence && !currentReport.homework && !currentReport.weeknesses && (
                  <div className={styles.typeBadge}>
                    <span className={styles.badgeIcon}>📝</span>
                    <span>تقرير عام</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Test Score Card */}
          {currentReport.test_degree && (
            <div className={styles.infoCard}>
              <div className={styles.cardHeader}>
                <FaBook />
                <h3>درجة الاختبار</h3>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.testScore}>
                  <span className={styles.scoreValue}>{currentReport.test_degree}</span>
                  <span className={styles.scoreLabel}>من 100</span>
                </div>
                <div className={styles.scoreBar}>
                  <div 
                    className={styles.scoreFill}
                    style={{ width: `${currentReport.test_degree}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {/* Teacher Notes Card */}
          {currentReport.teacher_notes && (
            <div className={styles.infoCard}>
              <div className={styles.cardHeader}>
                <FaClipboardList />
                <h3>ملاحظات المعلم</h3>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.teacherNotes}>
                  <p>{currentReport.teacher_notes}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className={styles.actionsSection}>
            <button className={styles.backButton} onClick={handleBack}>
              العودة للصفحة السابقة
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportDetails;
